/**
 * Pure helpers behind the arrows on the metrics page (TRA-1592): one pair
 * next to the window picker moves every card to earlier or later times by
 * half the window, in whole buckets. The stretch lives in the URL as `?end=` next to
 * `?hours=`, so a shared link opens on the same one; no `end` is now. The
 * zoom inside the window is metricsZoom's.
 *
 * The maths is the replicas chart's (replicaPan) with a longer limit: the
 * main metrics read keeps 30 days of rollups, the replica history 7 days.
 * `now` is a parameter everywhere so the tests pin it.
 */

import {
  HOUR_MS,
  PAN_LIMIT_MS,
  atEnd,
  canStep,
  hoursToLoad,
  stepped,
  visibleSpan,
  type PanDirection,
  type PanState,
} from '@/components/models/home/replicaPan'
import type { TimeRange } from '@/components/models/home/replicaZoom'
import { MAX_METRICS_HOURS } from '@/components/models/home/scalingWindow'

/** The gateway keeps 30 days of rollups: how far back the page can be moved. */
export const METRICS_PAN_LIMIT_MS = MAX_METRICS_HOURS * HOUR_MS

const MINUTE_MS = 60 * 1000

/**
 * The bucket the main metrics read draws a window of `hours` at. Mirrors
 * `metrics_bucket` in the api's usage_views.py: the page needs it before
 * the read answers, to put `end` on the read's grid.
 */
export function metricsBucketMs(hours: number): number {
  if (hours <= 6) return 15 * MINUTE_MS
  if (hours <= 24) return 30 * MINUTE_MS
  if (hours <= 168) return HOUR_MS
  return 8 * HOUR_MS
}

/**
 * `end` floored onto the bucket grid of its window, or raised to the next
 * bucket with `up`; null when that is the bucket now sits in or later. The
 * server ends a window at a boundary, so an end on the grid makes every
 * card and the "Showing" line describe the same hours as the main read;
 * the bucket now is in is the window at now.
 */
export function onGrid(end: number, hours: number, now: number, up = false): number | null {
  const bucket = metricsBucketMs(hours)
  const snapped = (up ? Math.ceil(end / bucket) : Math.floor(end / bucket)) * bucket
  return snapped >= Math.floor(now / bucket) * bucket ? null : snapped
}

/**
 * The `?end=` a link carries, on the grid of its window, or null for now
 * when it is missing, not a time, not before now, or older than the 30
 * days the page can reach. The rule on the end alone, not the window's
 * start, keeps a stretch parked at the 30-day edge there as the clock
 * moves on.
 */
export function endFrom(raw: string | null, hours: number, now: number = Date.now()): number | null {
  if (raw === null) return null
  const parsed = Date.parse(raw)
  if (!Number.isFinite(parsed)) return null
  const end = onGrid(parsed, hours, now)
  return end === null || end < now - METRICS_PAN_LIMIT_MS ? null : end
}

/** Where the page's arrows can go, both against the same `now`, over the stretch shown. */
export interface PageView {
  span: TimeRange
  canEarlier: boolean
  canLater: boolean
}

/** The zoom when there is one, else the window ending at `end` or now. */
export function pageViewAt(state: PanState, hours: number, now: number = Date.now()): PageView {
  const span = visibleSpan(state, hours, now)
  return {
    span,
    canEarlier: canStep(span, 'earlier', now, METRICS_PAN_LIMIT_MS),
    canLater: canStep(span, 'later', now, METRICS_PAN_LIMIT_MS),
  }
}

/**
 * How far an arrow press moves the stretch: half the window, rounded down
 * to whole buckets (at least one), so a step back and a step forward land
 * on the same end instead of drifting a bucket each way.
 */
export function stepMs(hours: number): number {
  const bucket = metricsBucketMs(hours)
  return Math.max(bucket, Math.floor((hours * HOUR_MS) / 2 / bucket) * bucket)
}

/** The end after an arrow press: a step away on the grid, or null once the stretch reaches now. */
export function steppedEnd(
  end: number | null,
  hours: number,
  direction: PanDirection,
  now: number,
): number | null {
  const next = stepped(atEnd(end), hours, direction, now, METRICS_PAN_LIMIT_MS, stepMs(hours)).end
  return next === null ? null : onGrid(next, hours, now)
}

/** The page's search params with a new window: back at now, so `end` goes. */
export function paramsWithHours(prev: URLSearchParams, hours: number): URLSearchParams {
  const params = new URLSearchParams(prev)
  params.set('hours', String(hours))
  params.delete('end')
  return params
}

/** The page's search params with the stretch ending at `end`, or back at now. */
export function paramsWithEnd(prev: URLSearchParams, end: number | null): URLSearchParams {
  const params = new URLSearchParams(prev)
  if (end === null) {
    params.delete('end')
  } else {
    params.set('end', new Date(end).toISOString())
  }
  return params
}

/** True when the whole stretch shown is older than the 7 days of replica history the gateway keeps. */
export function pastReplicaHistory(end: number | null, now: number = Date.now()): boolean {
  return end !== null && end <= now - PAN_LIMIT_MS
}

/**
 * True when the window reaches further back than the 7 days of replica
 * history but ends inside them, so the chart draws only the part inside:
 * a window over 7 days, or one moved partly past them.
 */
export function replicaHistoryCut(end: number | null, hours: number, now: number = Date.now()): boolean {
  const start = (end ?? now) - hours * HOUR_MS
  return !pastReplicaHistory(end, now) && start < now - PAN_LIMIT_MS
}

/**
 * The hours back from now the engine read must cover so the stretch is
 * loaded: it only takes hours back from now, and keeps 30 days.
 */
export function engineHoursToLoad(end: number | null, hours: number, now: number = Date.now()): number {
  return hoursToLoad(atEnd(end), hours, now, MAX_METRICS_HOURS)
}
